import React, { useState, useEffect, useMemo } from 'react';
import { 
  Layout, 
  Tabs, 
  Button, 
  Modal, 
  Descriptions, 
  Tag, 
  message, 
  Typography,
  Row,
  Col,
  Card,
  Space,
  Switch,
  Input,
  Table,
  Select,
  Alert,
  App,
  Spin
} from 'antd';
import { 
  EyeOutlined, 
  DownloadOutlined, 
  FileOutlined,
  DeleteOutlined,
  PrinterOutlined,
  SettingOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClientAdmin } from '../../context/ClientAdminContext';
import { useAuth } from '../../context/AuthContext';
import { useActiveForms } from '../../context/ActiveFormsContext';
import NavigationBar from '../../components/NavigationBar';
import RockNMaltaTab from './ClientAdminForms/RockNMalta';
import GhannejjaGoddaTab from './ClientAdminForms/GhannejjaGodda';
import DanceOnTab from './ClientAdminForms/DanceOn';
import Dfm2026Tab from './ClientAdminForms/Dfm2026';
import FormWorkspace from './ClientAdminForms/FormWorkspace';
import AutocompleteInput from '../../components/AutocompleteInput';
import { API_ORIGIN } from '../../config';
import { openApiFile } from '../../utils/apiAuth';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const ClientAdmin = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientAdmin, setClientAdmin, logout } = useClientAdmin();
  const { user } = useAuth();
  const { activeForms, fetchActiveForms } = useActiveForms();
  
  // Determine user's applicationForm - backend uses 'name' for the username field
  let userApplicationForm = clientAdmin?.applicationForm;
  const userName = clientAdmin?.name;
  
  // Fallback: If applicationForm is not set, derive it from userName
  if (!userApplicationForm && userName) {
    if (userName.includes('festghana') || userName.includes('ghannejja')) {
      userApplicationForm = 'Għannejja Ġodda';
    } else if (userName.includes('rocknmalta')) {
      userApplicationForm = 'RockNMalta';
    } else if (userName.includes('danceon')) {
      userApplicationForm = 'DanceOn';
    }
  }
  
  // Initialize state based on current userName from sessionStorage
  const getInitialTabState = () => {
    if (!userName) {
      return {
        activeTab: 'rocknmalta',
        tabDisabled: { rockNMalta: false, ghannejja: false, danceOn: false }
      };
    }
    
    // Check if this is a dynamic form user (not one of the hardcoded forms)
    const isDynamicForm = userApplicationForm && 
      userApplicationForm !== 'RockNMalta' && 
      userApplicationForm !== 'Għannejja Ġodda' && 
      userApplicationForm !== 'DanceOn' &&
      userApplicationForm !== 'DFM2026';
    
    if (isDynamicForm) {
      return {
        activeTab: 'dynamic-form',
        tabDisabled: { dfm2026: true, rockNMalta: true, ghannejja: true, danceOn: true }
      };
    }

    // Check for DFM2026
    if (userApplicationForm === 'DFM2026' || userName.includes('dfm')) {
      return {
        activeTab: 'dfm2026',
        tabDisabled: { dfm2026: false, rockNMalta: true, ghannejja: true, danceOn: true }
      };
    }
    
    if (userName.includes('festghana') || userName.includes('ghannejja')) {
      return {
        activeTab: 'ghannejja',
        tabDisabled: { rockNMalta: true, ghannejja: false, danceOn: true }
      };
    } else if (userName.includes('rocknmalta')) {
      return {
        activeTab: 'rocknmalta',
        tabDisabled: { rockNMalta: false, ghannejja: true, danceOn: true }
      };
    } else if (userName.includes('danceon')) {
      return {
        activeTab: 'danceon',
        tabDisabled: { rockNMalta: true, ghannejja: true, danceOn: false }
      };
    }
    
    return {
      activeTab: 'rocknmalta',
      tabDisabled: { rockNMalta: false, ghannejja: false, danceOn: false }
    };
  };
  
  const [activeTab, setActiveTab] = useState(() => {
    const initialState = getInitialTabState();
    return initialState.activeTab;
  });
  const [tabDisabled, setTabDisabled] = useState(() => {
    const initialState = getInitialTabState();
    return initialState.tabDisabled;
  });
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [rockMaltaApplications, setRockMaltaApplications] = useState([]);
  const [danceOnSubmissions, setDanceOnSubmissions] = useState([]);
  const [ghannejjaApplications, setGhannejjaApplications] = useState([]);
  const [dfm2026Applications, setDfm2026Applications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedGhannejja, setSelectedGhannejja] = useState(null);
  const [selectedDfm2026, setSelectedDfm2026] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [dfm2026ModalVisible, setDfm2026ModalVisible] = useState(false);
  const [submissionModalVisible, setSubmissionModalVisible] = useState(false);
  const [ghannejjaModalVisible, setGhannejjaModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [danceOnConfigModalVisible, setDanceOnConfigModalVisible] = useState(false);
  const [danceOnConfig, setDanceOnConfig] = useState({
    internalRecipient: '',
    externalRecipient: ''
  });
  const [danceOnConfigLoading, setDanceOnConfigLoading] = useState(false);
  const [ghannejjaNotificationModalVisible, setGhannejjaNotificationModalVisible] = useState(false);
  const [ghannejjaNotifications, setGhannejjaNotifications] = useState([]);
  const [ghannejjaNotificationLoading, setGhannejjaNotificationLoading] = useState(false);
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [newNotificationType, setNewNotificationType] = useState('TO');
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetEmail, setDeleteTargetEmail] = useState('');
  const [paymentLinksModalVisible, setPaymentLinksModalVisible] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [paymentLinksLoading, setPaymentLinksLoading] = useState(false);
  const [editingPaymentLink, setEditingPaymentLink] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);
  const [canManageForms, setCanManageForms] = useState(false);
  
  // Check if user can manage client admins / create dynamic forms
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setCanManageForms(false);
          return;
        }

        const response = await fetch(`${API_ORIGIN}/api/employee/can-manage-client-admins`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
      setCanManageForms(data.canManageClientAdmins || false);
        } else {
          setCanManageForms(false);
        }
      } catch (error) {
        setCanManageForms(false);
      }
    };

    // Check permissions for both employee users and client admins
    if (user || clientAdmin) {
      checkPermissions();
    }
  }, [user, clientAdmin]);
  
  // Update active tab and disabled state when clientAdmin loads
  useEffect(() => {
    if (userName) {
      // Check if this is a dynamic form user
      const isDynamicForm = userApplicationForm && 
        userApplicationForm !== 'RockNMalta' && 
        userApplicationForm !== 'Għannejja Ġodda' && 
        userApplicationForm !== 'DanceOn' &&
        userApplicationForm !== 'DFM2026';
      
      if (isDynamicForm) {
        setActiveTab('dynamic-form');
        setTabDisabled({
          dfm2026: true,
          rockNMalta: true,
          ghannejja: true,
          danceOn: true
        });
      } else if (userApplicationForm === 'DFM2026' || userName.includes('dfm')) {
        setActiveTab('dfm2026');
        setTabDisabled({
          dfm2026: false,
          rockNMalta: true,
          ghannejja: true,
          danceOn: true
        });
      } else if (userName.includes('festghana') || userName.includes('ghannejja')) {
        setActiveTab('ghannejja');
        setTabDisabled({
          dfm2026: true,
          rockNMalta: true,
          ghannejja: false,
          danceOn: true
        });
      } else if (userName.includes('rocknmalta')) {
        setActiveTab('rocknmalta');
        setTabDisabled({
          dfm2026: true,
          rockNMalta: false,
          ghannejja: true,
          danceOn: true
        });
      } else if (userName.includes('danceon')) {
        setActiveTab('danceon');
        setTabDisabled({
          dfm2026: true,
          rockNMalta: true,
          ghannejja: true,
          danceOn: false
        });
      }
    }
  }, [userName, userApplicationForm]);
  
  // This useEffect already exists above with proper dependencies - remove this duplicate
  
  // CSV Export for RockNMalta
  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_ORIGIN}/api/excel-rnm/export`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rocknmalta_applications_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('CSV exported successfully');
    } catch (error) {
      message.error('Failed to export CSV');
    }
  };

  // Delete application
  const handleDeleteApplication = (applicationId) => {
    setApplicationToDelete(applicationId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!applicationToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/rock-malta-fund/delete-application/${applicationToDelete}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.success('Application deleted successfully');
        setDeleteModalVisible(false);
        setApplicationToDelete(null);
        // Refresh the applications list
        fetchRockMaltaApplications();
      } else {
        message.error(data.error || data.message || 'Failed to delete application');
      }
    } catch (error) {
      message.error('Failed to delete application. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setApplicationToDelete(null);
  };


  const confirmDeleteDanceOn = async () => {
    if (!applicationToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/danceon/submission/${applicationToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.success('Submission deleted successfully');
        setDeleteModalVisible(false);
        setApplicationToDelete(null);
        // Refresh the submissions list
        fetchDanceOnSubmissions();
      } else {
        message.error(data.error || data.message || 'Failed to delete submission');
      }
    } catch (error) {
      message.error('Failed to delete submission. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete DanceOn submission
  const handleDeleteDanceOn = (submissionId) => {
    setApplicationToDelete(submissionId);
    setDeleteModalVisible(true);
  };

  // Note: Removed auto-logout on unmount to preserve session

  // Fetch RockNMalta applications
  const fetchRockMaltaApplications = async () => {
    setLoading(true);
    try {
            const response = await fetch(`${API_ORIGIN}/api/rock-malta-fund/applications`);
      const data = await response.json();
      
      if (data.success) {
        setRockMaltaApplications(data.applications || []);
      } else {
        message.error('Failed to fetch applications: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to fetch applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch DanceOn submissions
  const fetchDanceOnSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/danceon/submissions`);
      const data = await response.json();
      
      if (data.success) {
        setDanceOnSubmissions(data.submissions || []);
      } else {
        message.error('Failed to fetch submissions: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to fetch submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Għannejja Ġodda applications
  const fetchGhannejjaApplications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/applications`);
      const data = await response.json();
      
      if (data.success) {
        setGhannejjaApplications(data.applications || []);
      } else {
        message.error('Failed to fetch applications: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to fetch applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch DanceOn email configuration
  const fetchDanceOnConfig = async () => {
    setDanceOnConfigLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/danceon/config`);
      const data = await response.json();
      if (response.ok && data.success) {
        // API returns: { success: true, config: { internalRecipient, externalRecipient, updatedAt } }
        const config = data.config || {};
        setDanceOnConfig({
          internalRecipient: config.internalRecipient || '',
          externalRecipient: config.externalRecipient || ''
        });
      }
    } catch (error) {
      message.error('Failed to fetch email configuration');
    } finally {
      setDanceOnConfigLoading(false);
    }
  };

  // Fetch Għannejja notification settings
  const fetchGhannejjaNotifications = async () => {
    setGhannejjaNotificationLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/notification-settings`);
      const data = await response.json();
      if (response.ok && data.success) {
        setGhannejjaNotifications(data.settings || []);
      } else {
        message.error('Failed to fetch notification settings');
      }
    } catch (error) {
      message.error('Failed to fetch notification settings');
    } finally {
      setGhannejjaNotificationLoading(false);
    }
  };



  // Add new Għannejja notification recipient
  const addGhannejjaNotification = async () => {
    if (!newNotificationEmail) {
      message.warning('Please enter an email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newNotificationEmail)) {
      message.warning('Please enter a valid email address');
      return;
    }

    setGhannejjaNotificationLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/notification-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newNotificationEmail,
          recipientType: newNotificationType
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        message.success('Notification recipient added successfully');
        setNewNotificationEmail('');
        setNewNotificationType('TO');
        fetchGhannejjaNotifications();
      } else {
        message.error(data.error || 'Failed to add notification recipient');
      }
    } catch (error) {
      message.error('Failed to add notification recipient');
    } finally {
      setGhannejjaNotificationLoading(false);
    }
  };

  // Update Għannejja notification recipient
  const updateGhannejjaNotification = async (id, updates) => {
    setGhannejjaNotificationLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/notification-settings/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const updateType = updates.recipientType ? 'Recipient type changed successfully' : 'Recipient status updated successfully';
        message.success(updateType);
        fetchGhannejjaNotifications();
      } else {
        message.error(data.error || data.message || 'Failed to update notification recipient');
        // Refresh to revert UI changes if update failed
        fetchGhannejjaNotifications();
      }
    } catch (error) {
      message.error('Failed to update notification recipient');
      // Refresh to revert UI changes if update failed
      fetchGhannejjaNotifications();
    } finally {
      setGhannejjaNotificationLoading(false);
    }
  };

  // Delete Għannejja notification recipient
  const deleteGhannejjaNotification = async (id) => {
    setGhannejjaNotificationLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/notification-settings/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        message.success('Notification recipient deleted successfully');
        fetchGhannejjaNotifications();
      } else {
        message.error(data.error || data.message || 'Failed to delete notification recipient');
      }
    } catch (error) {
      message.error(`Failed to delete notification recipient: ${error.message}`);
    } finally {
      setGhannejjaNotificationLoading(false);
    }
  };

  // Fetch all payment links
  const fetchPaymentLinks = async () => {
    setPaymentLinksLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/payment-links`);
      const data = await response.json();
      if (response.ok && data.success) {
        setPaymentLinks(data.paymentLinks || []);
      } else {
        message.error('Failed to fetch payment links');
      }
    } catch (error) {
      message.error('Failed to fetch payment links');
    } finally {
      setPaymentLinksLoading(false);
    }
  };

  // Fetch available forms
  const fetchAvailableForms = async () => {
    try {
      // Fetch all forms including dynamic forms
      const response = await fetch(`${API_ORIGIN}/api/forms`);
      const data = await response.json();
      if (response.ok && data.success) {
        setAvailableForms(data.forms || []);
      }
    } catch (error) {
      // silent fail
    }
  };

  // Add or update payment link
  const savePaymentLink = async (formId, paymentLink, hasLink) => {
    setPaymentLinksLoading(true);
    try {
      // Check if this is a dynamic form
      const isDynamicForm = userApplicationForm && 
        userApplicationForm !== 'RockNMalta' && 
        userApplicationForm !== 'Għannejja Ġodda' && 
        userApplicationForm !== 'DanceOn' &&
        userApplicationForm !== 'DFM2026';

      let response, data;

      if (isDynamicForm) {
        // Use dynamic forms API: PUT /api/forms/:formId
        response = await fetch(`${API_ORIGIN}/api/forms/${formId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            payment_link: paymentLink
          }),
        });
        data = await response.json();
      } else {
        // Use legacy payment-links API for static forms
        response = await fetch(`${API_ORIGIN}/api/payment-links`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clientFormId: formId,
            paymentLink: paymentLink,
            hasLink: hasLink
          }),
        });
        data = await response.json();
      }

      if (response.ok && data.success) {
        message.success('Payment setting saved successfully');
        await fetchPaymentLinks();
        await fetchAvailableForms(); // Refresh available forms list
        setEditingPaymentLink(null);
      } else {
        message.error(data.error || 'Failed to save payment setting');
        await fetchPaymentLinks(); // Refresh to revert UI
      }
    } catch (error) {
      message.error('Failed to save payment setting');
      await fetchPaymentLinks(); // Refresh to revert UI
    } finally {
      setPaymentLinksLoading(false);
    }
  };

  // Update payment link
  const updatePaymentLink = async (id, updates) => {
    setPaymentLinksLoading(true);
    try {
      // Check if this is a dynamic form
      const isDynamicForm = userApplicationForm && 
        userApplicationForm !== 'RockNMalta' && 
        userApplicationForm !== 'Għannejja Ġodda' && 
        userApplicationForm !== 'DanceOn' &&
        userApplicationForm !== 'DFM2026';

      let response, data;

      if (isDynamicForm) {
        // Use dynamic forms API: PUT /api/forms/:formId
        // For dynamic forms, 'id' is the form_id
        response = await fetch(`${API_ORIGIN}/api/forms/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            payment_link: updates.paymentLink
          }),
        });
        data = await response.json();
      } else {
        // Use legacy payment-links API for static forms
        response = await fetch(`${API_ORIGIN}/api/payment-links/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        });
        data = await response.json();
      }

      if (response.ok && data.success) {
        message.success('Payment setting updated successfully');
        // Wait a moment for database to commit
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchPaymentLinks();
      } else {
        message.error(data.error || 'Failed to update payment setting');
        await fetchPaymentLinks(); // Refresh to revert UI changes
      }
    } catch (error) {
      message.error('Failed to update payment setting');
      await fetchPaymentLinks(); // Refresh to revert UI changes
    } finally {
      setPaymentLinksLoading(false);
    }
  };

  // Delete payment link
  const deletePaymentLink = async (id) => {
    setPaymentLinksLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/payment-links/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        message.success('Payment link deleted successfully');
        fetchPaymentLinks();
      } else {
        message.error(data.error || 'Failed to delete payment link');
      }
    } catch (error) {
      message.error('Failed to delete payment link');
    } finally {
      setPaymentLinksLoading(false);
    }
  };

  // Update DanceOn email configuration
  const updateDanceOnConfig = async () => {
    setDanceOnConfigLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/danceon/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(danceOnConfig),
      });
      const data = await response.json();
      if (response.ok && data) {
        message.success('Email recipients updated successfully');
        setDanceOnConfigModalVisible(false);
      } else {
        message.error(data.error || 'Failed to update email recipients');
      }
    } catch (error) {
      message.error('Failed to update email recipients');
    } finally {
      setDanceOnConfigLoading(false);
    }
  };

  // Toggle form active status
  const toggleFormActive = async (formId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const response = await fetch(`${API_ORIGIN}/api/clientadmin/toggleForm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          id: formId, 
          isActive: newStatus 
        }),
      });
      const data = await response.json();
      
      if (data.success) {
        message.success(`Form ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        fetchActiveForms(); // Refresh the list
      } else {
        message.error('Failed to toggle form status: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to toggle form status. Please try again.');
    }
  };

  // Fetch specific application details
  const fetchApplicationDetails = async (id) => {
    try {
            const response = await fetch(`${API_ORIGIN}/api/rock-malta-fund/application/${id}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedApplication(data.application || data.data);
        setModalVisible(true);
      } else {
        message.error('Failed to fetch application details: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to fetch application details. Please try again.');
    }
  };

  // Fetch Għannejja application details
  const fetchGhannejjaDetails = async (id) => {
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/application/${id}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedGhannejja(data.application || data.data);
        setGhannejjaModalVisible(true);
      } else {
        message.error('Failed to fetch application details: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to fetch application details. Please try again.');
    }
  };

  // Update payment status for Għannejja application
  const updatePaymentStatus = async (id, hasPaid) => {
    setPaymentLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/payment/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hasPaid }),
      });
      const data = await response.json();
      
      if (data.success) {
        message.success(`Payment status updated successfully`);
        fetchGhannejjaApplications(); // Refresh the list
        if (ghannejjaModalVisible && selectedGhannejja?.id === id) {
          fetchGhannejjaDetails(id); // Refresh modal data
        }
      } else {
        message.error('Failed to update payment status: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      message.error('Failed to update payment status. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Delete Għannejja application
  const handleDeleteGhannejja = (applicationId) => {
    setApplicationToDelete(applicationId);
    setDeleteModalVisible(true);
  };

  const confirmDeleteGhannejja = async () => {
    if (!applicationToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/application/${applicationToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.success('Application deleted successfully');
        setDeleteModalVisible(false);
        setApplicationToDelete(null);
        setGhannejjaModalVisible(false);
        setSelectedGhannejja(null);
        fetchGhannejjaApplications(); // Refresh the list
      } else {
        message.error(data.error || data.message || 'Failed to delete application');
      }
    } catch (error) {
      message.error('Failed to delete application. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Download file helper
  const downloadFile = (filename) => {
    if (!filename) {
      message.warning('File not available');
      return;
    }
    
    // Staff-only file: fetched with the login, then opened (a plain link can't carry it)
    openApiFile(`${API_ORIGIN}/api/rock-malta-fund/uploads/${encodeURIComponent(filename)}`).catch((err) =>
      message.error(err.message || 'Could not open the file')
    );
  };

  // Print application
  const printApplication = async (application) => {
    try {
      message.loading({ content: 'Generating PDF...', key: 'pdf-generation' });
      
            const response = await fetch(`${API_ORIGIN}/api/rock-malta-fund/generate-pdf/${application.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Get the PDF blob
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open in new tab
        window.open(url, '_blank');
        
        message.success({ content: 'PDF generated successfully!', key: 'pdf-generation' });
        
        // Clean up
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const data = await response.json();
        message.error({ content: data.error || 'Failed to generate PDF', key: 'pdf-generation' });
      }
    } catch (error) {
      message.error({ content: 'Failed to generate PDF. Please try again.', key: 'pdf-generation' });
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Check if current user is allowed to delete
  const allowedToDelete = ['etienne.bonello@festivals.mt','francis.b.agius@festivals.mt','zach.zammit@festivals.mt'];
  const canDelete = allowedToDelete.includes(user?.email);

  // Fetch payment links and available forms on component mount
  useEffect(() => {
    fetchPaymentLinks();
    fetchAvailableForms();
  }, []);

  useEffect(() => {
    if (activeTab === 'dfm2026') {
      fetchDfm2026Applications();
    } else if (activeTab === 'rocknmalta') {
      fetchRockMaltaApplications();
    } else if (activeTab === 'danceon') {
      fetchDanceOnSubmissions();
    } else if (activeTab === 'ghannejja') {
      fetchGhannejjaApplications();
    }
  }, [activeTab]);

  // Fetch DFM2026 applications
  const fetchDfm2026Applications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ORIGIN}/api/dfm2026/applications`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setDfm2026Applications(data.applications || []);
      }
    } catch (error) {
      message.error('Failed to load DFM2026 applications');
    } finally {
      setLoading(false);
    }
  };

  // Memoize tabs items to prevent infinite re-renders
  const tabsItems = useMemo(() => {
    // Check if this is a dynamic form (not one of the hardcoded forms)
    const isDynamicForm = userApplicationForm && 
      userApplicationForm !== 'RockNMalta' && 
      userApplicationForm !== 'Għannejja Ġodda' && 
      userApplicationForm !== 'DanceOn' &&
      userApplicationForm !== 'DFM2026';

    // Dynamic forms are shown in their own workspace (FormWorkspace), not as a tab
    if (isDynamicForm) return [];

    // Otherwise, show the standard tabs
    return [
      {
        key: 'dfm2026',
        label: 'DFM2026',
        disabled: tabDisabled.dfm2026,
        children: (
          <Dfm2026Tab
            applications={dfm2026Applications} 
            loading={loading}
            canDelete={canDelete}
            onRefresh={fetchDfm2026Applications}
            onView={(record) => {
              setSelectedDfm2026(record);
              setDfm2026ModalVisible(true);
            }}
            onDelete={handleDeleteApplication}
            onDownloadVideo={(id) => {
              openApiFile(`${API_ORIGIN}/api/dfm2026/video/${id}`).catch((err) => message.error(err.message || 'Could not open the file'));
            }}
            onDownloadCV={(id) => {
              openApiFile(`${API_ORIGIN}/api/dfm2026/cv/${id}`).catch((err) => message.error(err.message || 'Could not open the file'));
            }}
            onNotificationClick={() => {
              message.info('Notifications feature coming soon');
            }}
            onEmailTemplatesClick={() => {
              message.info('Email templates feature coming soon');
            }}
            formatDate={formatDate}
          />
        )
      },
      {
        key: 'rocknmalta',
        label: 'Rock\'N Malta Fund 2026',
        disabled: tabDisabled.rockNMalta,
        children: (
          <RockNMaltaTab
            applications={rockMaltaApplications}
            loading={loading}
            canDelete={canDelete}
            onRefresh={fetchRockMaltaApplications}
            onView={fetchApplicationDetails}
            onPrint={printApplication}
            onDelete={handleDeleteApplication}
            onExportCSV={handleExportCSV}
            formatDate={formatDate}
          />
        )
      },
      {
        key: 'ghannejja',
        label: 'Għannejja Ġodda 2026',
        disabled: tabDisabled.ghannejja,
        children: (
          <GhannejjaGoddaTab
            applications={ghannejjaApplications}
            loading={loading}
            canDelete={canDelete}
            onRefresh={fetchGhannejjaApplications}
            onView={fetchGhannejjaDetails}
            onDelete={handleDeleteGhannejja}
            onNotificationClick={() => {
              setGhannejjaNotificationModalVisible(true);
              fetchGhannejjaNotifications();
            }}
          />
        )
      },
      {
        key: 'danceon',
        label: 'DanceOn 2026',
        disabled: tabDisabled.danceOn,
        children: (
          <DanceOnTab
            submissions={danceOnSubmissions}
            loading={loading}
            canDelete={canDelete}
            onRefresh={fetchDanceOnSubmissions}
            onView={(record) => {
              setSelectedSubmission(record);
              setSubmissionModalVisible(true);
            }}
            onDelete={handleDeleteDanceOn}
            onConfigClick={() => {
              setDanceOnConfigModalVisible(true);
              fetchDanceOnConfig();
            }}
            formatDate={formatDate}
          />
        )
      }
    ];
  }, [
    userApplicationForm, 
    availableForms, 
    tabDisabled,
    dfm2026Applications,
    rockMaltaApplications,
    loading,
    ghannejjaApplications,
    danceOnSubmissions,
    canDelete,
    canManageForms
  ]);

  // DFM2026 details modal content
  const renderDfm2026Details = () => {
    if (!selectedDfm2026) return null;

    const app = selectedDfm2026;
    
    return (
      <div>
        <Row gutter={[24, 24]}>
          {/* Personal Information */}
          <Col span={24}>
            <Card title="Personal Information" size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Application ID">{app.id}</Descriptions.Item>
                <Descriptions.Item label="Submitted">{formatDate(app.submittedAt)}</Descriptions.Item>
                <Descriptions.Item label="Name">{app.name} {app.surname}</Descriptions.Item>
                <Descriptions.Item label="Age">{app.age}</Descriptions.Item>
                <Descriptions.Item label="Email">{app.email}</Descriptions.Item>
                <Descriptions.Item label="Contact Number">{app.contactNumber}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* CV Information */}
          <Col span={24}>
            <Card title="CV / Experience" size="small">
              {app.cvPath ? (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="CV File">
                    <Button 
                      icon={<DownloadOutlined />}
                      onClick={() => openApiFile(`${API_ORIGIN}/api/dfm2026/cv/${app.id}`).catch((err) => message.error(err.message || 'Could not open the file'))}
                    >
                      Download CV
                    </Button>
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="CV Text">
                    <Paragraph>{app.cvText || 'N/A'}</Paragraph>
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          </Col>

          {/* Video Submission */}
          <Col span={24}>
            <Card title="Video Submission" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Video">
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={() => openApiFile(`${API_ORIGIN}/api/dfm2026/video/${app.id}`).catch((err) => message.error(err.message || 'Could not open the file'))}
                    type="primary"
                  >
                    Download Video
                  </Button>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Application details modal content
  const renderApplicationDetails = () => {
    if (!selectedApplication) return null;

    const app = selectedApplication;
    
    return (
      <div>
        <Row gutter={[24, 24]}>
          {/* Basic Information */}
          <Col span={24}>
            <Card title="Basic Information" size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Application ID">{app.id}</Descriptions.Item>
                <Descriptions.Item label="Submitted">{formatDate(app.created_at)}</Descriptions.Item>
                <Descriptions.Item label="Organisation Name">{app.organisation_name}</Descriptions.Item>
                <Descriptions.Item label="Representative">{app.representee}</Descriptions.Item>
                <Descriptions.Item label="Applicant Name">{app.applicant_name}</Descriptions.Item>
                <Descriptions.Item label="Contact Number">{app.contact_number}</Descriptions.Item>
                <Descriptions.Item label="Email Address">{app.email_address}</Descriptions.Item>
                <Descriptions.Item label="Category">{app.category_of_applicant}</Descriptions.Item>
                <Descriptions.Item label="Estimated Cost" span={2}>
                  <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                    €{parseFloat(app.estimated_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Project Details */}
          <Col span={24}>
            <Card title="Project Details" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Event Description">
                  <Paragraph>{app.event_description}</Paragraph>
                </Descriptions.Item>
                <Descriptions.Item label="Artists Proposed">
                  <Paragraph>{app.artists_proposed}</Paragraph>
                </Descriptions.Item>
                <Descriptions.Item label="Venue Proposed">{app.venue_proposed}</Descriptions.Item>
                <Descriptions.Item label="Event Date">{app.event_date || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Category Applicant Name">{app.category_applicant_name || 'N/A'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Declarations */}
          <Col span={24}>
            <Card title="Declarations" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Health & Safety Declaration">
                  {app.health_safety_declaration === '1' || app.health_safety_declaration ? 
                    <Tag color="green">Declared</Tag> : 
                    <Tag color="red">Not Declared</Tag>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="GBER Declaration">
                  {app.declaration_gber === '1' || app.declaration_gber ? 
                    <Tag color="green">Declared</Tag> : 
                    <Tag color="red">Not Declared</Tag>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Outstanding Recovery Orders Declaration">
                  {app.declaration_outstanding_orders === '1' || app.declaration_outstanding_orders ? 
                    <Tag color="green">Declared</Tag> : 
                    <Tag color="red">Not Declared</Tag>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Legally Entitled to Operate in Malta">
                  {app.declaration_operate === '1' || app.declaration_operate ? 
                    <Tag color="green">Declared</Tag> : 
                    <Tag color="red">Not Declared</Tag>
                  }
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* File Attachments */}
          <Col span={24}>
            <Card title="File Attachments" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                {/* ID Cards */}
                {app.applicant_id_card && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>ID Card (File 1)</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.applicant_id_card)}
                    >
                      Download
                    </Button>
                  </div>
                )}
                
                {app.applicant_id_card_2 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>ID Card (File 2)</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.applicant_id_card_2)}
                    >
                      Download
                    </Button>
                  </div>
                )}
                
                {/* Tax Compliance */}
                {app.tax_compliance_doc && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Tax Compliance Document</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.tax_compliance_doc)}
                    >
                      Download
                    </Button>
                  </div>
                )}
                
                {/* Organisation/Role Chart */}
                {app.organisation_structure_history && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Organisation/Role Chart</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.organisation_structure_history)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {/* Business Plan Documents */}
                <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                  <Text strong style={{ color: '#1890ff' }}>Business Plan Documents</Text>
                </div>

                {app.upload_financial_model && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Business and Financial Model</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_financial_model)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.upload_ticket_prices && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Ticket Prices</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_ticket_prices)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.upload_comprehensive_list && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Revenue Streams (Comprehensive List)</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_comprehensive_list)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.upload_itemised_list && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Expenses and Costs (Itemised List)</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_itemised_list)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.upload_projected_pl && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Projected Profit and Loss Report</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_projected_pl)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.upload_anticipated_audience && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Anticipated Audience</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_anticipated_audience)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.upload_declaration_intent && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Insurance Declaration</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.upload_declaration_intent)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {/* Project Management and Marketing */}
                <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                  <Text strong style={{ color: '#1890ff' }}>Project Documents</Text>
                </div>

                {app.project_management_plan && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Project Management Plan</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.project_management_plan)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {app.marketing_plan && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>Marketing Plan</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.marketing_plan)}
                    >
                      Download
                    </Button>
                  </div>
                )}

                {/* State Aid Declaration (Optional) */}
                {app.de_minimis_declaration_form && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Space>
                      <FileOutlined />
                      <Text>State Aid Declaration (Optional)</Text>
                    </Space>
                    <Button 
                      size="small" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadFile(app.de_minimis_declaration_form)}
                    >
                      Download
                    </Button>
                  </div>
                )}
                
                {!app.applicant_id_card && !app.tax_compliance_doc && !app.organisation_role_chart && 
                 !app.upload_financial_model && !app.upload_ticket_prices && !app.upload_comprehensive_list && 
                 !app.upload_itemised_list && !app.upload_projected_pl && !app.upload_anticipated_audience && 
                 !app.upload_declaration_intent && !app.project_management_plan && !app.marketing_plan && 
                 !app.de_minimis_declaration_form && (
                  <Text type="secondary">No files attached</Text>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Check if user has just authenticated via navigation state or sessionStorage
  const hasStoredSession = sessionStorage.getItem('clientAdmin');
  const isAuthenticated = clientAdmin || location.state?.authenticated || hasStoredSession;

  // If not authenticated, show access denied message
  if (!isAuthenticated) {
    return (
      <Layout>
        <NavigationBar />
        <Content style={{ padding: '24px 50px', minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ maxWidth: '500px', textAlign: 'center' }}>
            <Title level={3}>Access Restricted</Title>
            <Paragraph>
              Please log in as a Client Admin to access this page.
            </Paragraph>
            <Button type="primary" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </Card>
        </Content>
      </Layout>
    );
  }

  // Dynamic forms get their own workspace instead of the tabbed page below
  const isDynamicFormUser = userApplicationForm &&
    userApplicationForm !== 'RockNMalta' &&
    userApplicationForm !== 'Għannejja Ġodda' &&
    userApplicationForm !== 'DanceOn' &&
    userApplicationForm !== 'DFM2026';

  if (isDynamicFormUser) {
    const dynamicForm = availableForms.find(form => form.form_name === userApplicationForm);
    if (!dynamicForm) {
      return (
        <Layout>
          <NavigationBar />
          <Content style={{ padding: '24px 50px', minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {availableForms.length === 0 ? (
              <Spin size="large" />
            ) : (
              <Card style={{ maxWidth: '500px', textAlign: 'center' }}>
                <Title level={3}>Form not found</Title>
                <Paragraph>The form "{userApplicationForm}" is not in the list of dynamic forms.</Paragraph>
                <Button type="primary" onClick={() => navigate('/client-admin')}>Back to client admin</Button>
              </Card>
            )}
          </Content>
        </Layout>
      );
    }
    return (
      <FormWorkspace
        formId={dynamicForm.form_id}
        formName={userApplicationForm}
        slug={dynamicForm.form_slug}
        canDelete={canDelete}
        canManageForms={canManageForms}
        onLogout={() => {
          logout();
          message.success('Logged out successfully');
          navigate('/dashboard');
        }}
      />
    );
  }

  return (
    <Layout>
      <NavigationBar />
      <Content style={{ 
        padding: '24px 50px', 
        minHeight: 'calc(100vh - 64px)' 
      }}
      className="responsive-content"
      >
        <div style={{ 
          background: '#fff', 
          padding: '24px', 
          borderRadius: '8px' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <Title level={2} style={{ margin: 0 }}>Client Applications</Title>
            <Space>
              <Button 
                icon={<LogoutOutlined />}
                onClick={() => {
                  logout();
                  message.success('Logged out successfully');
                  navigate('/dashboard');
                }}
                danger
              >
                Logout
              </Button>
              {/* Hide Payment Links Settings and Form Settings buttons when viewing dynamic form tabs */}
              {activeTab !== 'dynamic-form' && (
                <>
                  <Button 
                    icon={<SettingOutlined />}
                    onClick={() => {
                      setPaymentLinksModalVisible(true);
                      fetchPaymentLinks();
                      fetchAvailableForms();
                    }}
                  >
                    Payment Links Settings
                  </Button>
                  {canManageForms && (
                    <Button 
                      icon={<SettingOutlined />}
                      onClick={() => {
                        setSettingsModalVisible(true);
                        fetchActiveForms();
                        fetchPaymentLinks();
                        fetchAvailableForms();
                      }}
                    >
                      Form Settings
                    </Button>
                  )}
                </>
              )}
            </Space>
          </div>
          
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            size="large"
            items={tabsItems}
          />
        </div>
      </Content>

      {/* Application Details Modal */}
      <Modal
        title={`Application Details - ${selectedApplication?.organisation_name || 'N/A'}`}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedApplication(null);
        }}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {renderApplicationDetails()}
      </Modal>

      {/* DFM2026 Details Modal */}
      <Modal
        title={`DFM2026 Application - ${selectedDfm2026?.name || ''} ${selectedDfm2026?.surname || ''}`}
        open={dfm2026ModalVisible}
        onCancel={() => {
          setDfm2026ModalVisible(false);
          setSelectedDfm2026(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDfm2026ModalVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
        style={{ top: 20 }}
      >
        {renderDfm2026Details()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Application"
        open={deleteModalVisible}
        onOk={activeTab === 'danceon' ? confirmDeleteDanceOn : activeTab === 'ghannejja' ? confirmDeleteGhannejja : confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ 
          danger: true,
          loading: deleteLoading 
        }}
        cancelButtonProps={{ 
          disabled: deleteLoading 
        }}
      >
        <p>Are you sure you want to delete this {activeTab === 'danceon' ? 'submission' : 'application'}?</p>
        <p style={{ color: '#ff4d4f', fontWeight: 500 }}>This action cannot be undone.</p>
      </Modal>

      {/* DanceOn Submission Details Modal */}
      <Modal
        title="DanceOn Submission Details"
        open={submissionModalVisible}
        onCancel={() => {
          setSubmissionModalVisible(false);
          setSelectedSubmission(null);
        }}
        footer={[
          <Button key="close" onClick={() => setSubmissionModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedSubmission && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedSubmission.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{`${selectedSubmission.name} ${selectedSubmission.surname}`}</Descriptions.Item>
            <Descriptions.Item label="Age">{selectedSubmission.age}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedSubmission.email}</Descriptions.Item>
            <Descriptions.Item label="Contact">{selectedSubmission.contact}</Descriptions.Item>
            <Descriptions.Item label="Interest">{selectedSubmission.interest}</Descriptions.Item>
            {selectedSubmission.background && (
              <Descriptions.Item label="Background">{selectedSubmission.background}</Descriptions.Item>
            )}
            <Descriptions.Item label="Submitted">{formatDate(selectedSubmission.submittedAt)}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* DanceOn Email Configuration Modal */}
      <Modal
        title="DanceOn Email Recipients"
        open={danceOnConfigModalVisible}
        onCancel={() => {
          setDanceOnConfigModalVisible(false);
        }}
        onOk={updateDanceOnConfig}
        confirmLoading={danceOnConfigLoading}
        width={600}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>Internal Recipient Email</Text>
            <Input
              type="email"
              placeholder="internal@domain.com"
              value={danceOnConfig?.internalRecipient || ''}
              onChange={(e) => setDanceOnConfig({ ...danceOnConfig, internalRecipient: e.target.value })}
              style={{ marginTop: 8 }}
              disabled={danceOnConfigLoading}
            />
            {danceOnConfig?.internalRecipient && (
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                Current: {danceOnConfig.internalRecipient}
              </Text>
            )}
          </div>
          <div>
            <Text strong>External Recipient Email</Text>
            <Input
              type="email"
              placeholder="external@domain.com"
              value={danceOnConfig?.externalRecipient || ''}
              onChange={(e) => setDanceOnConfig({ ...danceOnConfig, externalRecipient: e.target.value })}
              style={{ marginTop: 8 }}
              disabled={danceOnConfigLoading}
            />
            {danceOnConfig?.externalRecipient && (
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                Current: {danceOnConfig.externalRecipient}
              </Text>
            )}
          </div>
        </Space>
      </Modal>

      {/* Settings Modal for Active Forms */}
      <Modal
        title="Form Settings"
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSettingsModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Form Configuration"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  <strong>Form Active:</strong> Enable or disable the entire form. When inactive, users cannot submit applications.
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Payment Enabled:</strong> When enabled, users will be redirected to a payment link after submitting their application. Configure the payment URL in "Payment Links Settings".
                </p>
              </div>
            }
            type="info"
            showIcon
          />
          
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button 
                  onClick={() => {
                    fetchActiveForms();
                    fetchPaymentLinks();
                  }}
                  loading={loading || paymentLinksLoading}
                >
                  Refresh
                </Button>
                <Text type="secondary">
                  Total Forms: {activeForms.filter(form => form.applicationForm === userApplicationForm).length}
                </Text>
              </Space>
            </div>
        
            <Table
          columns={[
            {
              title: 'Form Name',
              dataIndex: 'applicationForm',
              key: 'applicationForm',
              render: (text) => <Text strong>{text || 'N/A'}</Text>
            },
            {
              title: 'Form Active',
              key: 'toggle',
              render: (_, record) => (
                <Switch
                  checked={record.activeForm === 1}
                  onChange={() => toggleFormActive(record.id, record.activeForm)}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              ),
              width: 150,
            },
            {
              title: 'Payment Enabled',
              key: 'paymentToggle',
              render: (_, record) => {
                // Match payment link by applicationForm - need to use the correct property name
                const paymentLink = paymentLinks.find(pl => pl.applicationForm === record.applicationForm);
                return (
                  <Switch
                    checked={paymentLink ? (paymentLink.hasLink === 1 || paymentLink.hasLink === true) : false}
                    onChange={async (checked) => {
                      if (paymentLink) {
                        // Update existing payment link - only change hasLink, keep the URL
                        await updatePaymentLink(paymentLink.id, { 
                          hasLink: checked ? 1 : 0
                        });
                      } else {
                        // Create new payment link entry with hasLink but no URL yet
                        const form = availableForms.find(f => f.applicationForm === record.applicationForm);
                        if (form) {
                          await savePaymentLink(form.id, null, checked ? 1 : 0);
                        } else {
                          message.error('Could not find form configuration');
                        }
                      }
                    }}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    loading={paymentLinksLoading}
                    disabled={paymentLinksLoading}
                  />
                );
              },
              width: 150,
            },
          ]}
          dataSource={activeForms.filter(form => form.applicationForm === userApplicationForm)}
          rowKey="id"
          loading={loading || paymentLinksLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} forms`,
          }}
        />
          </div>
        </Space>
      </Modal>

      {/* Għannejja Ġodda Application Details Modal */}
      <Modal
        title="Għannejja Ġodda Application Details"
        open={ghannejjaModalVisible}
        onCancel={() => {
          setGhannejjaModalVisible(false);
          setSelectedGhannejja(null);
        }}
        footer={[
          <Button 
            key="payment" 
            type={selectedGhannejja?.hasPaid ? 'default' : 'primary'}
            loading={paymentLoading}
            onClick={() => updatePaymentStatus(selectedGhannejja?.id, !selectedGhannejja?.hasPaid)}
          >
            {selectedGhannejja?.hasPaid ? 'Mark as Unpaid' : 'Mark as Paid'}
          </Button>,
          canDelete && (
            <Button 
              key="delete"
              danger
              loading={deleteLoading}
              onClick={() => handleDeleteGhannejja(selectedGhannejja?.id)}
            >
              Delete
            </Button>
          ),
          <Button key="close" onClick={() => setGhannejjaModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedGhannejja && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedGhannejja.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{`${selectedGhannejja.fName} ${selectedGhannejja.lName}`}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedGhannejja.email}</Descriptions.Item>
            <Descriptions.Item label="Telephone">{selectedGhannejja.telNo}</Descriptions.Item>
            <Descriptions.Item label="ID Card">{selectedGhannejja.idCard}</Descriptions.Item>
            <Descriptions.Item label="Age">{selectedGhannejja.age}</Descriptions.Item>
            <Descriptions.Item label="Address">{selectedGhannejja.address}</Descriptions.Item>
            <Descriptions.Item label="Locality">{selectedGhannejja.localityName || selectedGhannejja.locality}</Descriptions.Item>
            <Descriptions.Item label="Interest">{selectedGhannejja.interest}</Descriptions.Item>
            <Descriptions.Item label="Options Selected">
              <div>
                {selectedGhannejja.option1 === 1 && <Tag color="blue">Familjari mad-dinja tal-għana</Tag>}
                {selectedGhannejja.option2 === 1 && <Tag color="green">Nkanta ġeneri oħrajn</Tag>}
                {selectedGhannejja.option3 === 1 && <Tag color="orange">Ma nkantax</Tag>}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Status">
              <Tag color={selectedGhannejja.hasPaid ? 'success' : 'warning'}>
                {selectedGhannejja.hasPaid ? 'Paid (€5)' : 'Pending Payment'}
              </Tag>
            </Descriptions.Item>
            {selectedGhannejja.age < 16 && (
              <>
                <Descriptions.Item label="Guardian Name">{`${selectedGhannejja.guardianFName || 'N/A'} ${selectedGhannejja.guardianLName || 'N/A'}`}</Descriptions.Item>
                <Descriptions.Item label="Guardian Email">{selectedGhannejja.guardianEmail || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Guardian Tel">{selectedGhannejja.guardianTelNo || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Guardian ID">{selectedGhannejja.guardianIdCard || 'N/A'}</Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Submitted">{new Date(selectedGhannejja.submittedAt).toLocaleString()}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Għannejja Notification Settings Modal */}
      <Modal
        title="Għannejja Ġodda Notification Settings"
        open={ghannejjaNotificationModalVisible}
        onCancel={() => setGhannejjaNotificationModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setGhannejjaNotificationModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Add New Recipient Section */}
          <Card title="Add New Notification Recipient" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Search Employee by Name or Email</Text>
                <AutocompleteInput
                  apiEndpoint="/api/ghannejja/search-employees"
                  placeholder="Search employee or enter email..."
                  onSelect={(employee) => setNewNotificationEmail(employee.email)}
                  value={newNotificationEmail}
                  onChange={setNewNotificationEmail}
                  disabled={ghannejjaNotificationLoading}
                  style={{ marginTop: 8 }}
                />
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                  Search by name or enter email address directly
                </Text>
              </div>
              <div>
                <Text strong>Recipient Type</Text>
                <Select
                  value={newNotificationType}
                  onChange={setNewNotificationType}
                  style={{ width: '100%', marginTop: 8 }}
                  disabled={ghannejjaNotificationLoading}
                >
                  <Select.Option value="TO">TO - Primary Recipient</Select.Option>
                  <Select.Option value="CC">CC - Carbon Copy</Select.Option>
                </Select>
              </div>
              <Button
                type="primary"
                onClick={addGhannejjaNotification}
                loading={ghannejjaNotificationLoading}
                block
              >
                Add Recipient
              </Button>
            </Space>
          </Card>

          {/* Current Recipients Table */}
          <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: 16 }}>Current Notification Recipients</Text>
              <Button
                onClick={fetchGhannejjaNotifications}
                loading={ghannejjaNotificationLoading}
                size="small"
              >
                Refresh
              </Button>
            </div>
            <Table
              columns={[
                {
                  title: 'Email',
                  dataIndex: 'email',
                  key: 'email',
                  render: (email) => <Text strong>{email}</Text>
                },
                {
                  title: 'Type',
                  dataIndex: 'recipientType',
                  key: 'recipientType',
                  width: 120,
                  render: (type) => (
                    <Tag color={type === 'TO' ? 'blue' : 'green'}>
                      {type}
                    </Tag>
                  )
                },
                {
                  title: 'Status',
                  dataIndex: 'isActive',
                  key: 'isActive',
                  width: 100,
                  render: (isActive, record) => (
                    <Switch
                      checked={isActive === 1}
                      onChange={(checked) => updateGhannejjaNotification(record.id, { isActive: checked ? 1 : 0 })}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                      loading={ghannejjaNotificationLoading}
                    />
                  )
                },
                {
                  title: 'Change Type',
                  key: 'changeType',
                  width: 120,
                  render: (_, record) => (
                    <Select
                      value={record.recipientType}
                      onChange={(value) => updateGhannejjaNotification(record.id, { recipientType: value })}
                      style={{ width: '100%' }}
                      size="small"
                      disabled={ghannejjaNotificationLoading}
                    >
                      <Select.Option value="TO">TO</Select.Option>
                      <Select.Option value="CC">CC</Select.Option>
                    </Select>
                  )
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 100,
                  render: (_, record) => (
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setDeleteTargetId(record.id);
                        setDeleteTargetEmail(record.email);
                        setDeleteConfirmVisible(true);
                      }}
                      disabled={ghannejjaNotificationLoading}
                    >
                      Delete
                    </Button>
                  )
                }
              ]}
              dataSource={ghannejjaNotifications}
              rowKey="id"
              loading={ghannejjaNotificationLoading}
              pagination={false}
              size="small"
            />
          </div>

          <Alert
            message="How Notifications Work"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  <strong>TO Recipients:</strong> Primary recipients who receive the notification directly.
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>CC Recipients:</strong> Carbon copy recipients who are kept in the loop.
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Active Status:</strong> Only active recipients will receive notifications. Use the toggle to temporarily disable notifications without deleting the recipient.
                </p>
              </div>
            }
            type="info"
            showIcon
          />
        </Space>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Notification Recipient"
        open={deleteConfirmVisible}
        onOk={async () => {
          await deleteGhannejjaNotification(deleteTargetId);
          setDeleteConfirmVisible(false);
          setDeleteTargetId(null);
          setDeleteTargetEmail('');
        }}
        onCancel={() => {
          setDeleteConfirmVisible(false);
          setDeleteTargetId(null);
          setDeleteTargetEmail('');
        }}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to remove <strong>{deleteTargetEmail}</strong> from the notification list?</p>
      </Modal>

      {/* Payment Links Settings Modal */}
      <Modal
        title="Payment Links Settings"
        open={paymentLinksModalVisible}
        onCancel={() => {
          setPaymentLinksModalVisible(false);
          setEditingPaymentLink(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setPaymentLinksModalVisible(false);
            setEditingPaymentLink(null);
          }}>
            Close
          </Button>
        ]}
        width={900}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Payment Links Management"
            description={`Configure payment link for ${userApplicationForm}. Enable the payment requirement toggle and add a payment link URL. Users will be prompted to pay when submitting their application.`}
            type="info"
            showIcon
          />

          {/* Add Payment Link Section - Only if form doesn't have one yet */}
          {!paymentLinks.find(pl => pl.applicationForm === userApplicationForm) && userApplicationForm && (
            <Card title={`Add Payment Link for ${userApplicationForm}`} size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Payment Link URL</Text>
                  <Input
                    value={editingPaymentLink?.paymentLink || ''}
                    onChange={(e) => setEditingPaymentLink({ 
                      ...editingPaymentLink,
                      paymentLink: e.target.value,
                      formName: userApplicationForm
                    })}
                    placeholder="Enter payment link URL..."
                    style={{ marginTop: 8 }}
                    onPressEnter={() => {
                      if (editingPaymentLink?.paymentLink) {
                        // Need to get the clientFormId from availableForms
                        const form = availableForms.find(f => f.applicationForm === userApplicationForm);
                        if (form) {
                          savePaymentLink(form.id, editingPaymentLink.paymentLink, 1);
                        }
                      }
                    }}
                  />
                </div>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (editingPaymentLink?.paymentLink) {
                        const form = availableForms.find(f => f.applicationForm === userApplicationForm);
                        if (form) {
                          savePaymentLink(form.id, editingPaymentLink.paymentLink, 1);
                        } else {
                          message.error('Could not find form configuration');
                        }
                      } else {
                        message.warning('Please enter a payment link URL');
                      }
                    }}
                    loading={paymentLinksLoading}
                  >
                    Add Payment Link
                  </Button>
                  <Button
                    onClick={() => setEditingPaymentLink(null)}
                  >
                    Cancel
                  </Button>
                </Space>
              </Space>
            </Card>
          )}

          <Table
            columns={[
              {
                title: 'Form Name',
                dataIndex: 'applicationForm',
                key: 'applicationForm',
                render: (applicationForm) => <Text strong>{applicationForm}</Text>
              },
              {
                title: 'Payment Link',
                dataIndex: 'paymentLink',
                key: 'paymentLink',
                render: (paymentLink, record) => {
                  if (editingPaymentLink?.id === record.id) {
                    return (
                      <Space.Compact style={{ width: '100%' }}>
                        <Input
                          value={editingPaymentLink.paymentLink || ''}
                          onChange={(e) => setEditingPaymentLink({ ...editingPaymentLink, paymentLink: e.target.value })}
                          placeholder="Enter payment link URL..."
                          onPressEnter={() => {
                            if (editingPaymentLink.paymentLink) {
                              // For dynamic forms, get form_id from availableForms
                              const isDynamicForm = userApplicationForm && 
                                userApplicationForm !== 'RockNMalta' && 
                                userApplicationForm !== 'Għannejja Ġodda' && 
                                userApplicationForm !== 'DanceOn' &&
                                userApplicationForm !== 'DFM2026';
                              
                              let idToUse;
                              if (isDynamicForm) {
                                const form = availableForms.find(f => f.applicationForm === userApplicationForm || f.form_name === userApplicationForm);
                                idToUse = form?.form_id || form?.id;
                              } else {
                                idToUse = editingPaymentLink.id;
                              }
                              
                              updatePaymentLink(idToUse, {
                                paymentLink: editingPaymentLink.paymentLink,
                                hasLink: 1
                              });
                              setEditingPaymentLink(null);
                            }
                          }}
                        />
                        <Button
                          type="primary"
                          onClick={() => {
                            if (editingPaymentLink.paymentLink) {
                              const isDynamicForm = userApplicationForm && 
                                userApplicationForm !== 'RockNMalta' && 
                                userApplicationForm !== 'Għannejja Ġodda' && 
                                userApplicationForm !== 'DanceOn' &&
                                userApplicationForm !== 'DFM2026';
                              
                              let idToUse;
                              if (isDynamicForm) {
                                const form = availableForms.find(f => f.applicationForm === userApplicationForm || f.form_name === userApplicationForm);
                                idToUse = form?.form_id || form?.id;
                              } else {
                                idToUse = editingPaymentLink.id;
                              }
                              
                              updatePaymentLink(idToUse, {
                                paymentLink: editingPaymentLink.paymentLink,
                                hasLink: 1
                              });
                              setEditingPaymentLink(null);
                            }
                          }}
                          loading={paymentLinksLoading}
                        >
                          Save
                        </Button>
                        <Button
                          onClick={() => setEditingPaymentLink(null)}
                        >
                          Cancel
                        </Button>
                      </Space.Compact>
                    );
                  }
                  
                  return paymentLink ? (
                    <Space>
                      <a href={paymentLink} target="_blank" rel="noopener noreferrer" style={{ maxWidth: 300, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {paymentLink}
                      </a>
                      <Button
                        size="small"
                        onClick={() => setEditingPaymentLink({ ...record })}
                      >
                        Edit
                      </Button>
                    </Space>
                  ) : (
                    <Text type="secondary" italic>
                      Click Edit to add payment link
                    </Text>
                  );
                }
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_, record) => {
                  const isPaymentEnabled = record.hasLink === 1 || record.hasLink === true;
                  return (
                    <Space>
                      <Button
                        size="small"
                        onClick={() => setEditingPaymentLink({ ...record })}
                      >
                        Edit
                      </Button>
                      {record.paymentLink && isPaymentEnabled && (
                        <Button
                          size="small"
                          danger
                          onClick={() => {
                            modal.confirm({
                              title: 'Clear Payment Link',
                              content: `Remove payment link for ${record.applicationForm}?`,
                              onOk: async () => {
                                const isDynamicForm = userApplicationForm && 
                                  userApplicationForm !== 'RockNMalta' && 
                                  userApplicationForm !== 'Għannejja Ġodda' && 
                                  userApplicationForm !== 'DanceOn' &&
                                  userApplicationForm !== 'DFM2026';
                                
                                let idToUse;
                                if (isDynamicForm) {
                                  const form = availableForms.find(f => f.applicationForm === userApplicationForm || f.form_name === userApplicationForm);
                                  idToUse = form?.form_id || form?.id;
                                } else {
                                  idToUse = record.id;
                                }
                                
                                await updatePaymentLink(idToUse, { paymentLink: null, hasLink: 0 });
                              }
                            });
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </Space>
                  );
                }
              }
            ]}
            dataSource={paymentLinks.filter(pl => pl.applicationForm === userApplicationForm)}
            rowKey="id"
            loading={paymentLinksLoading}
            pagination={false}
            size="small"
          />
        </Space>
      </Modal>
    </Layout>
  );
};

export default ClientAdmin;
